import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// Which site this is: the server issues a sign-in for this site only.
export const APP_NAME = "store";
const APP_HEADERS = { "X-PB-App": APP_NAME };

// Sign-in (step 2 sessions). The short-lived access token lives only in this
// page's memory, never in localStorage. The long-lived refresh token is an
// HttpOnly cookie the browser sends to /api/auth only; JavaScript can't read
// it. A non-secret flag remembers that someone is signed in on this site.
let accessToken = null;
const SIGNED_IN_FLAG = "pm_signed_in";
const LEGACY_TOKEN = "pm_token";   // sign-ins from before sessions

export function setAccessToken(token) {
  accessToken = token || null;
  try {
    if (token) localStorage.setItem(SIGNED_IN_FLAG, "1");
    else localStorage.removeItem(SIGNED_IN_FLAG);
  } catch (e) { /* storage unavailable */ }
}

export function hasAccessToken() {
  return !!accessToken;
}

function storageGet(key) {
  try { return localStorage.getItem(key); } catch (e) { return null; }
}

// Calls that sign in or use the sign-in say which site this is.
const SIGN_IN_URL = /^\/(auth\/|vendors\/register|delivery-partners\/register|community\/ngos\/register|care\/salons\/register)/;
const api = axios.create({ baseURL: API_BASE, withCredentials: true });

api.interceptors.request.use((cfg) => {
  if (accessToken) cfg.headers.Authorization = `Bearer ${accessToken}`;
  // Sign-in calls say which site this is (other calls don't need to).
  if (SIGN_IN_URL.test(cfg.url || "")) cfg.headers["X-PB-App"] = APP_NAME;
  return cfg;
});

// One refresh at a time, across tabs too (each tab would otherwise spend the
// same cookie, which the server treats as a stolen token).
let refreshing = null;
export function refreshSession() {
  if (!refreshing) {
    const run = async () => {
      const { data } = await axios.post(`${API_BASE}/auth/refresh`, null, { withCredentials: true, headers: APP_HEADERS });
      setAccessToken(data.access_token);
      return data.access_token;
    };
    refreshing = (async () => {
      try {
        if (typeof navigator !== "undefined" && navigator.locks?.request) {
          return await navigator.locks.request(`pb-refresh-${APP_NAME}`, run);
        }
        return await run();
      } finally {
        refreshing = null;
      }
    })();
  }
  return refreshing;
}

// On page load: resume the session from the cookie, or move a sign-in from
// before sessions (a token in localStorage) to a session. Returns true when
// signed in.
export async function resumeSession() {
  const legacy = storageGet(LEGACY_TOKEN);
  if (legacy) {
    try { localStorage.removeItem(LEGACY_TOKEN); } catch (e) { /* storage unavailable */ }
    try {
      const { data } = await axios.post(`${API_BASE}/auth/session/upgrade`, null,
        { withCredentials: true, headers: { ...APP_HEADERS, Authorization: `Bearer ${legacy}` } });
      setAccessToken(data.access_token);
      return true;
    } catch (e) {
      setAccessToken(null);
      return false;
    }
  }
  // Even without a sign-in on this site, ask once: one sign-in covers the
  // shop, Care and Community, so a session on a sibling site signs you in here.
  try {
    await refreshSession();
    return true;
  } catch (e) {
    if (storageGet(SIGNED_IN_FLAG)) setAccessToken(null);
    return false;
  }
}

// "Confirm it's you" before a risky change: the app registers a dialog that
// resolves true once the server accepted the password or SMS code.
let reauthHandler = null;
export function setReauthHandler(fn) {
  reauthHandler = fn;
}

function expireSession(error) {
  setAccessToken(null);
  window.dispatchEvent(new CustomEvent("pm:session-expired", {
    detail: formatApiErrorDetail(error?.response?.data?.detail),
  }));
}

// G3: a signed-in request that comes back 401 (access token expired) gets one
// silent refresh and is retried; if the session itself has ended, the app is
// told so it can send the user to the login page.
const AUTH_ENDPOINTS = /\/auth\/(login|register|forgot-password|reset-password|refresh|session\/upgrade|phone\/login|reauth)\b/;
api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const status = error?.response?.status;
    const cfg = error?.config || {};
    const url = cfg.url || "";
    const detail = error?.response?.data?.detail;
    if (status === 401 && accessToken && !AUTH_ENDPOINTS.test(url) && !cfg._retried) {
      try {
        await refreshSession();
        return api({ ...cfg, _retried: true });
      } catch (e) {
        expireSession(error);
      }
    } else if (status === 401 && cfg._retried) {
      expireSession(error);
    } else if (status === 403 && detail?.code === "reauth_required" && reauthHandler && !cfg._reauthed) {
      if (await reauthHandler(detail)) return api({ ...cfg, _reauthed: true });
    }
    return Promise.reject(error);
  },
);

// Turns any FastAPI `detail` (string, validation list, or structured object
// such as rate-limit / blocked-account errors) into a readable sentence.
export function formatApiErrorDetail(detail) {
  const fallback = "Something went wrong. Please try again.";
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const text = detail
      .map((e) => {
        if (typeof e === "string") return e;
        if (e && typeof e.msg === "string") {
          const field = Array.isArray(e.loc) ? e.loc.filter((p) => p !== "body" && typeof p === "string").pop() : null;
          return field ? `${field.replace(/_/g, " ")}: ${e.msg}` : e.msg;
        }
        return null;
      })
      .filter(Boolean)
      .join(" ");
    return text || fallback;
  }
  if (typeof detail === "object") {
    if (detail.error === "rate_limited") {
      const wait = Number(detail.retry_after_seconds) || 60;
      return `Too many attempts. Please wait ${wait} second${wait === 1 ? "" : "s"} and try again.`;
    }
    for (const key of ["message", "msg", "detail", "error"]) {
      if (typeof detail[key] === "string" && detail[key]) return detail[key];
    }
  }
  return fallback;
}

// Opens a protected file (KYC document, invoice PDF…) in a new tab without
// putting the login token in the URL: the file is fetched with the normal
// Authorization header and shown from a local blob URL.
export async function openProtectedFile(path) {
  // Open the tab synchronously so popup blockers allow it.
  const win = window.open("", "_blank");
  try {
    const rel = path.startsWith("/api/") ? path.slice(4) : path;
    const { data, headers } = await api.get(rel, { responseType: "blob" });
    const blob = data instanceof Blob ? data : new Blob([data], { type: headers?.["content-type"] });
    const url = URL.createObjectURL(blob);
    if (win) win.location.href = url;
    else window.location.assign(url);
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (err) {
    if (win) win.close();
    throw err;
  }
}

export default api;

// Pictures uploaded to PawBazaar are "/api/files/…" on the backend; others are
// full https:// links.
export function mediaUrl(p) {
  if (!p) return null;
  return p.startsWith("/") ? `${BACKEND_URL}${p}` : p;
}
